import { useState, useEffect, useMemo } from 'react';
import {
  IconPlus,
  IconRefresh,
  IconReload,
  IconX,
  IconDeviceFloppy,
  IconSearch,
  IconLoader2,
  IconCheck,
  IconSelector,
  IconAlertCircle,
  IconCircleCheck,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import { formatDateTime } from '../utils/dateFormat';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertAction } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group';

interface MerchantAccount {
  id: number;
  distributor_i_customer: number;
  merchant_name: string;
  contact_email_address: string;
  contact_phone_number: string;
  contact_first_name: string;
  contact_last_name: string;
  wallet_merchant_id: string | null;
  wallet_snsi: string | null;
  wallet_account_number: string | null;
  created_at: string;
  updated_at: string;
  distributor_name: string;
}

interface Distributor {
  i_customer: number;
  company_name: string;
}

interface FormData {
  distributor_i_customer: number | null;
  merchant_name: string;
  contact_email_address: string;
  contact_phone_number: string;
  contact_first_name: string;
  contact_last_name: string;
}

const emptyForm: FormData = {
  distributor_i_customer: null,
  merchant_name: '',
  contact_email_address: '',
  contact_phone_number: '',
  contact_first_name: '',
  contact_last_name: '',
};

export function MerchantAccountsPage() {
  const { hasWriteAccess } = useAuth();
  const canWrite = hasWriteAccess('wallet');
  const [accounts, setAccounts] = useState<MerchantAccount[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterDistributor, setFilterDistributor] = useState<Distributor | null>(null);
  const [filterDistOpen, setFilterDistOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [formDistOpen, setFormDistOpen] = useState(false);

  useEffect(() => {
    loadAccounts();
    loadDistributors();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/merchant-accounts');
      setAccounts(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load merchant accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadDistributors = async () => {
    try {
      const res = await api.get('/merchant-accounts/distributors');
      setDistributors(res.data || []);
    } catch (err: any) {
      console.error('Failed to load distributors:', err);
    }
  };

  const handleOpenDrawer = () => {
    setFormData({ ...emptyForm });
    setSaveError('');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setFormData({ ...emptyForm });
    setSaveError('');
  };

  const [warning, setWarning] = useState('');
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSuccess('');
    setWarning('');

    try {
      const res = await api.post('/merchant-accounts', formData);
      const record = res.data;

      if (record.silicon_error) {
        setWarning(
          `Merchant account saved to database, but Silicon registration failed: ${record.silicon_error}. ` +
          `The wallet fields can be populated later.`,
        );
      } else {
        setSuccess(
          `Merchant account created and registered with Silicon (Merchant ID: ${record.wallet_merchant_id}, Account: ${record.wallet_account_number})`,
        );
      }

      handleCloseDrawer();
      loadAccounts();
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to create merchant account');
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async (account: MerchantAccount) => {
    setRetryingId(account.id);
    setError('');
    setSuccess('');
    setWarning('');

    try {
      const res = await api.post(`/merchant-accounts/${account.id}/retry`);
      const record = res.data;

      setSuccess(
        `Silicon registration complete for ${account.merchant_name} (Merchant ID: ${record.wallet_merchant_id}, Account: ${record.wallet_account_number})`,
      );
      loadAccounts();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          `Failed to register ${account.merchant_name} with Silicon`,
      );
    } finally {
      setRetryingId(null);
    }
  };

  const isFormValid =
    formData.distributor_i_customer &&
    formData.merchant_name.trim() &&
    formData.contact_email_address.trim() &&
    formData.contact_phone_number.trim() &&
    formData.contact_first_name.trim() &&
    formData.contact_last_name.trim();

  const selectedDistributor = distributors.find(
    (d) => d.i_customer === formData.distributor_i_customer,
  ) || null;

  const filteredAccounts = useMemo(() => {
    let result = accounts;

    if (filterDistributor) {
      result = result.filter(
        (a) => a.distributor_i_customer === filterDistributor.i_customer,
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.merchant_name?.toLowerCase().includes(q) ||
          a.contact_first_name?.toLowerCase().includes(q) ||
          a.contact_last_name?.toLowerCase().includes(q) ||
          a.contact_email_address?.toLowerCase().includes(q) ||
          a.contact_phone_number?.includes(q) ||
          a.wallet_merchant_id?.toLowerCase().includes(q) ||
          a.wallet_account_number?.toLowerCase().includes(q) ||
          a.distributor_name?.toLowerCase().includes(q) ||
          String(a.id).includes(q),
      );
    }

    return result;
  }, [accounts, filterDistributor, searchQuery]);

  const hasActiveFilters = !!filterDistributor || !!searchQuery.trim();

  return (
    <div>
      <PageHeader
        title="Merchant Accounts"
        subtitle="Manage distributor merchant accounts for wallet integration"
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadAccounts}
              disabled={loading}
            >
              <IconRefresh className="size-4" data-icon="inline-start" />
              Refresh
            </Button>
            <Button
              variant="default"
              onClick={handleOpenDrawer}
            >
              <IconPlus className="size-4" data-icon="inline-start" />
              Add Merchant Account
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-3">
          <IconAlertCircle />
          <AlertDescription>{error}</AlertDescription>
          <AlertAction>
            <Button variant="ghost" size="icon-xs" onClick={() => setError('')}>
              <IconX />
            </Button>
          </AlertAction>
        </Alert>
      )}
      {success && (
        <Alert className="mb-3">
          <IconCircleCheck className="text-emerald-500" />
          <AlertDescription>{success}</AlertDescription>
          <AlertAction>
            <Button variant="ghost" size="icon-xs" onClick={() => setSuccess('')}>
              <IconX />
            </Button>
          </AlertAction>
        </Alert>
      )}
      {warning && (
        <Alert className="mb-3">
          <IconAlertTriangle className="text-amber-500" />
          <AlertDescription>{warning}</AlertDescription>
          <AlertAction>
            <Button variant="ghost" size="icon-xs" onClick={() => setWarning('')}>
              <IconX />
            </Button>
          </AlertAction>
        </Alert>
      )}

      <Card className="mb-3">
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <InputGroup className="min-w-[280px]">
              <InputGroupAddon>
                <InputGroupText>
                  <IconSearch className="size-4 text-muted-foreground" />
                </InputGroupText>
              </InputGroupAddon>
              <InputGroupInput
                placeholder="Search merchant accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    size="icon-xs"
                    onClick={() => setSearchQuery('')}
                  >
                    <IconX className="size-3.5" />
                  </InputGroupButton>
                </InputGroupAddon>
              )}
            </InputGroup>

            <Popover open={filterDistOpen} onOpenChange={setFilterDistOpen}>
              <PopoverTrigger
                render={
                  <Button
                    variant="outline"
                    className="min-w-[300px] justify-between font-normal"
                  />
                }
              >
                {filterDistributor
                  ? `${filterDistributor.company_name} (${filterDistributor.i_customer})`
                  : 'Filter by Distributor'}
                <IconSelector className="ml-auto size-4 shrink-0 opacity-50" />
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0">
                <Command>
                  <CommandInput placeholder="Search distributors..." />
                  <CommandList>
                    <CommandEmpty>No distributors found.</CommandEmpty>
                    <CommandGroup>
                      {distributors.map((dist) => (
                        <CommandItem
                          key={dist.i_customer}
                          data-checked={filterDistributor?.i_customer === dist.i_customer}
                          onSelect={() => {
                            setFilterDistributor(
                              filterDistributor?.i_customer === dist.i_customer ? null : dist,
                            );
                            setFilterDistOpen(false);
                          }}
                        >
                          {dist.company_name} ({dist.i_customer})
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setFilterDistributor(null);
                }}
              >
                <IconX className="size-4" data-icon="inline-start" />
                Clear filters
              </Button>
            )}
            <span className="text-sm text-muted-foreground sm:ml-auto">
              {filteredAccounts.length} of {accounts.length} accounts
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && accounts.length === 0 ? (
            <div className="flex justify-center p-4">
              <IconLoader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : accounts.length === 0 ? (
            <Alert>
              <IconInfoCircle />
              <AlertDescription>
                No merchant accounts found. Click &quot;Add Merchant Account&quot; to create one.
              </AlertDescription>
            </Alert>
          ) : filteredAccounts.length === 0 ? (
            <Alert>
              <IconInfoCircle />
              <AlertDescription>
                No merchant accounts match your filters. Try adjusting your search or distributor filter.
              </AlertDescription>
            </Alert>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Distributor</TableHead>
                  <TableHead>Merchant Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Merchant ID</TableHead>
                  <TableHead>Account Number</TableHead>
                  <TableHead>Created</TableHead>
                  {canWrite && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.id}</TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">
                        {account.distributor_name}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        ID: {account.distributor_i_customer}
                      </span>
                    </TableCell>
                    <TableCell>{account.merchant_name}</TableCell>
                    <TableCell>
                      {account.contact_first_name} {account.contact_last_name}
                    </TableCell>
                    <TableCell>{account.contact_email_address}</TableCell>
                    <TableCell>{account.contact_phone_number}</TableCell>
                    <TableCell>
                      {account.wallet_merchant_id ? (
                        <Badge variant="outline" className="border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
                          {account.wallet_merchant_id}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {account.wallet_account_number || (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs">
                        {formatDateTime(account.created_at)}
                      </span>
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        {!account.wallet_merchant_id ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRetry(account)}
                            disabled={retryingId === account.id}
                          >
                            {retryingId === account.id ? (
                              <IconLoader2 className="size-4 animate-spin" data-icon="inline-start" />
                            ) : (
                              <IconReload className="size-4" data-icon="inline-start" />
                            )}
                            {retryingId === account.id ? 'Retrying...' : 'Retry'}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={drawerOpen} onOpenChange={(open) => { if (!open) handleCloseDrawer(); }}>
        <SheetContent side="right" className="w-full sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle>Add Merchant Account</SheetTitle>
          </SheetHeader>
          <Separator />

          <div className="flex-1 overflow-y-auto p-4">
            {saveError && (
              <Alert variant="destructive" className="mb-4">
                <IconAlertCircle />
                <AlertDescription>{saveError}</AlertDescription>
                <AlertAction>
                  <Button variant="ghost" size="icon-xs" onClick={() => setSaveError('')}>
                    <IconX />
                  </Button>
                </AlertAction>
              </Alert>
            )}

            <div className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label>
                  Distributor <span className="text-destructive">*</span>
                </Label>
                <Popover open={formDistOpen} onOpenChange={setFormDistOpen}>
                  <PopoverTrigger
                    render={
                      <Button
                        variant="outline"
                        className="w-full justify-between font-normal"
                      />
                    }
                  >
                    {selectedDistributor
                      ? `${selectedDistributor.company_name} (${selectedDistributor.i_customer})`
                      : 'Search for a distributor...'}
                    <IconSelector className="ml-auto size-4 shrink-0 opacity-50" />
                  </PopoverTrigger>
                  <PopoverContent className="w-[--anchor-width] p-0">
                    <Command>
                      <CommandInput placeholder="Search distributors..." />
                      <CommandList>
                        <CommandEmpty>No distributors found.</CommandEmpty>
                        <CommandGroup>
                          {distributors.map((dist) => (
                            <CommandItem
                              key={dist.i_customer}
                              data-checked={selectedDistributor?.i_customer === dist.i_customer}
                              onSelect={() => {
                                setFormData({
                                  ...formData,
                                  distributor_i_customer:
                                    selectedDistributor?.i_customer === dist.i_customer
                                      ? null
                                      : dist.i_customer,
                                });
                                setFormDistOpen(false);
                              }}
                            >
                              {dist.company_name} ({dist.i_customer})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="merchant_name">
                  Merchant Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="merchant_name"
                  value={formData.merchant_name}
                  onChange={(e) =>
                    setFormData({ ...formData, merchant_name: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label htmlFor="contact_first_name">
                    First Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact_first_name"
                    value={formData.contact_first_name}
                    onChange={(e) =>
                      setFormData({ ...formData, contact_first_name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact_last_name">
                    Last Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact_last_name"
                    value={formData.contact_last_name}
                    onChange={(e) =>
                      setFormData({ ...formData, contact_last_name: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact_email">
                  Email Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email_address}
                  onChange={(e) =>
                    setFormData({ ...formData, contact_email_address: e.target.value })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact_phone">
                  Phone Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone_number}
                  onChange={(e) =>
                    setFormData({ ...formData, contact_phone_number: e.target.value })
                  }
                  placeholder="27..."
                />
                <p className="text-xs text-muted-foreground">
                  Include country code, e.g. 27821234567
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleCloseDrawer} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={saving || !isFormValid}
                >
                  {saving ? (
                    <IconLoader2 className="size-4 animate-spin" data-icon="inline-start" />
                  ) : (
                    <IconDeviceFloppy className="size-4" data-icon="inline-start" />
                  )}
                  {saving ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
